import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  useKundeAendern,
  useKunden,
  useKundenarten,
  useSpielplatzAnlegen,
  useSpielplaetze,
} from '../api/abfragen'
import { EIGENKONTROLLETEXT, type Eigenkontrolle } from '../api/typen'
import { Bearbeiten, type Feld } from '../bausteine/Bearbeiten'
import { Fehlerhinweis, Ladeanzeige, Leerhinweis, datumDeutsch } from '../bausteine/Hinweise'
import { darf, darfSchreiben, useKonto } from '../konto/KontoKontext'
import { Dokumente } from '../kundenakte/Dokumente'
import { Kontakte } from '../kundenakte/Kontakte'
import { Vertraege } from '../kundenakte/Vertraege'
import { Portalzugaenge } from '../bausteine/Portalzugaenge'
import { KundeLoeschen } from '../bausteine/KundeLoeschen'

/* Die Akte eines Kunden hat vier Seiten. Nebeneinander untergebracht wären
   sie auf dem Handy eine endlose Rolle; als Reiter ist immer nur das da,
   wonach gerade gesucht wird. Der Reiter steht im Zustand und nicht in der
   Adresse: wer aus einem Spielplatz zurückkommt, landet dort, wo er war. */
type Reiter = 'plaetze' | 'dokumente' | 'kontakte' | 'vertraege' | 'zugaenge'

const REITER: { wert: Reiter; text: string }[] = [
  { wert: 'plaetze', text: 'Spielplätze' },
  { wert: 'dokumente', text: 'Dokumente' },
  { wert: 'kontakte', text: 'Kontakte' },
  { wert: 'vertraege', text: 'Verträge' },
]

/* Die Portalzugänge standen früher in der Verwaltung. Ein Zugang gehört zu
   genau einem Kunden; ihn an zwei Orten zu führen hieß, den Kunden ein
   zweites Mal heraussuchen zu müssen.

   Der Reiter erscheint nur, wer sie auch verwalten darf. Sonst führte er
   auf eine Seite voller Absagen – und das ist keine Auskunft. */
const ZUGANGSREITER: { wert: Reiter; text: string } = {
  wert: 'zugaenge',
  text: 'Portalzugänge',
}

export function KundeDetail() {
  const { kundeId = '' } = useParams()
  const { konto } = useKonto()
  const kunden = useKunden()
  const plaetze = useSpielplaetze(kundeId)
  const anlegen = useSpielplatzAnlegen()
  const aendern = useKundeAendern()
  const [formularOffen, setzeFormularOffen] = useState(false)
  const [bearbeiten, setzeBearbeiten] = useState(false)
  const [reiter, setzeReiter] = useState<Reiter>('plaetze')
  const [loeschen, setzeLoeschen] = useState(false)
  const gehZu = useNavigate()
  const arten = useKundenarten()

  const kunde = kunden.data?.find((k) => k.id === kundeId)

  // Alle Hooks stehen vor dem ersten `return`. Genau hier standen zwei
  // davon darunter, und der Linter hat es gefunden — dieselbe Fehlerklasse,
  // die die Anwendung heute früh zur weißen Seite gemacht hat.
  if (kunden.isLoading || plaetze.isLoading) return <Ladeanzeige />
  if (plaetze.error) return <Fehlerhinweis fehler={plaetze.error} />
  if (!kunde) return <Leerhinweis titel="Kunde nicht gefunden" />

  const sichtbareReiter = darf(konto, 'portal_verwalten')
    ? [...REITER, ZUGANGSREITER]
    : REITER

  return (
    <div className="space-y-4">
      <nav className="text-base">
        <Link to="/kunden" className="text-marke-700 underline">
          ← Kunden
        </Link>
      </nav>

      <header className="flex flex-wrap items-start gap-3">
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{kunde.name}</h1>
          <p className="text-slate-700">
            {kunde.art ?? '—'}
            {kunde.kundennummer ? ` · ${kunde.kundennummer}` : ''}
          </p>
        </div>
        {darf(konto, 'kunden_loeschen') && (
          <button
            type="button"
            className="knopf-warnung"
            onClick={() => setzeLoeschen(true)}
          >
            Löschen
          </button>
        )}
        {darfSchreiben(konto) && (
          <button type="button" className="knopf-still" onClick={() => setzeBearbeiten(true)}>
            Bearbeiten
          </button>
        )}
      </header>

      <div className="karte space-y-1 text-base">
        {(kunde.strasse || kunde.ort) && (
          <p>
            {kunde.strasse}
            {kunde.strasse && (kunde.plz || kunde.ort) ? ', ' : ''}
            {kunde.plz} {kunde.ort}
          </p>
        )}
        {kunde.ansprechpartner && <p>Ansprechpartner: {kunde.ansprechpartner}</p>}
        {kunde.email && <p>E-Mail: {kunde.email}</p>}
        {kunde.telefon && <p>Telefon: {kunde.telefon}</p>}
        {kunde.offene_maengel > 0 && (
          <p className="font-semibold text-stufe-3">
            {kunde.offene_maengel} offene {kunde.offene_maengel === 1 ? 'Mangel' : 'Mängel'}
          </p>
        )}
      </div>

      {/* Waagerecht rollbar: auf einem schmalen Handy passen vier Reiter
          nicht nebeneinander, und ein Umbruch säße mitten im Wort. */}
      <nav className="-mx-4 overflow-x-auto px-4">
        <div className="flex min-w-max gap-1 border-b-2 border-slate-200">
          {sichtbareReiter.map((r) => (
            <button
              key={r.wert}
              type="button"
              onClick={() => setzeReiter(r.wert)}
              className={`border-b-4 px-4 py-2 font-semibold ${
                reiter === r.wert
                  ? 'border-akzent-500 text-akzent-700'
                  : 'border-transparent text-slate-600'
              }`}
              style={{ minHeight: '48px' }}
              aria-current={reiter === r.wert ? 'page' : undefined}
            >
              {r.text}
            </button>
          ))}
        </div>
      </nav>

      {reiter === 'dokumente' && <Dokumente kundeId={kundeId} />}
      {reiter === 'kontakte' && <Kontakte kundeId={kundeId} />}
      {reiter === 'vertraege' && <Vertraege kundeId={kundeId} />}
      {reiter === 'zugaenge' && <Portalzugaenge fuerKunde={kundeId} />}

      {loeschen && (
        <KundeLoeschen
          kundeId={kundeId}
          name={kunde.name}
          schliessen={() => setzeLoeschen(false)}
          // Zurück zur Liste: die Seite, auf der man steht, gibt es nicht mehr.
          geloescht={() => gehZu('/kunden')}
        />
      )}

      <section hidden={reiter !== 'plaetze'}>
        <div className="mb-2 flex items-center gap-3">
          {darfSchreiben(konto) && (
            <button
              type="button"
              className="knopf-zweit ml-auto"
              onClick={() => setzeFormularOffen((o) => !o)}
            >
              {formularOffen ? 'Abbrechen' : 'Neuer Spielplatz'}
            </button>
          )}
        </div>

        {formularOffen && (
          <form
            className="karte mb-3 space-y-3"
            onSubmit={(e) => {
              e.preventDefault()
              const formular = new FormData(e.currentTarget)
              const roh = Object.fromEntries(
                [...formular.entries()].filter(([, w]) => String(w).trim() !== ''),
              )
              // Zahlenfelder umwandeln – ein leeres Feld darf nicht als "" gehen.
              const daten = {
                ...roh,
                kunde_id: kundeId,
                baujahr: roh.baujahr ? Number(roh.baujahr) : undefined,
                // Die Fläche wird beim Anlegen nicht mehr erfragt – auf dem
                // Platz misst sie niemand, und für die Prüfung ist sie ohne
                // Belang. Nachtragen geht weiterhin über „Bearbeiten“.
              }
              anlegen.mutate(daten as never, {
                onSuccess: () => {
                  setzeFormularOffen(false)
                  anlegen.reset()
                },
              })
            }}
          >
            <div>
              <label htmlFor="name">Bezeichnung</label>
              <input id="name" name="name" required placeholder="Spielplatz Am Deich" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="routenziel">Routenziel für die Anfahrt</label>
                <textarea
                  id="routenziel"
                  name="routenziel"
                  rows={2}
                  placeholder="Deichweg 12, 26736 Pewsum – Zufahrt über den Feldweg neben dem Sportplatz"
                />
                {/* Der Platzhalter hieß „4". Das las sich wie eine
                    Hausnummer und sagte nichts darüber, wofür das Feld da
                    ist: die Postadresse eines Spielplatzes führt oft nicht
                    zur Zufahrt. */}
                <p className="mt-1 text-base text-slate-600">
                  Was ins Navigationsgerät eingegeben wird, wenn die Anschrift
                  nicht zum Tor führt. Steht im Bericht und im Kalendertermin.
                </p>
              </div>
              <div>
                <label htmlFor="strasse">Straße</label>
                <input id="strasse" name="strasse" />
              </div>
              <div className="grid grid-cols-[7rem_1fr] gap-3">
                <div>
                  <label htmlFor="plz">PLZ</label>
                  <input id="plz" name="plz" inputMode="numeric" />
                </div>
                <div>
                  <label htmlFor="ort">Ort</label>
                  <input id="ort" name="ort" />
                </div>
              </div>
              <div>
                <label htmlFor="baujahr">Baujahr</label>
                <input id="baujahr" name="baujahr" inputMode="numeric" placeholder="2009" />
              </div>
            </div>

            <Fehlerhinweis fehler={anlegen.error} />

            <button type="submit" className="knopf-haupt" disabled={anlegen.isPending}>
              {anlegen.isPending ? 'Legt an …' : 'Spielplatz anlegen'}
            </button>
          </form>
        )}

        {(plaetze.data ?? []).length === 0 ? (
          <Leerhinweis titel="Noch keine Spielplätze" />
        ) : (
          <ul className="space-y-2">
            {(plaetze.data ?? []).map((p) => (
              <li key={p.id}>
                <Link to={`/spielplaetze/${p.id}`} className="karte-knopf">
                  <div className="flex-1">
                    <div className="font-semibold">
                      {p.kennung ? `${p.kennung} · ` : ''}
                      {p.name}
                    </div>
                    <div className="text-base text-slate-700">
                      {p.anzahl_geraete} Geräte
                      {p.letzte_pruefung_am
                        ? ` · zuletzt ${datumDeutsch(p.letzte_pruefung_am)}`
                        : ' · noch nicht geprüft'}
                    </div>
                  </div>
                  {p.offene_maengel > 0 && (
                    <span className="marke bg-stufe-3-grund text-stufe-3">
                      {p.offene_maengel}
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {bearbeiten && (
        <Bearbeiten
          titel="Kunde bearbeiten"
          untertitel={kunde.name}
          felder={kundenfelder(arten.data ?? [])}
          werte={kunde as unknown as Record<string, unknown>}
          laeuft={aendern.isPending}
          fehler={aendern.error}
          schliessen={() => {
            setzeBearbeiten(false)
            aendern.reset()
          }}
          speichern={(aenderungen) =>
            aendern.mutate({ id: kunde.id, ...aenderungen } as never, {
              onSuccess: () => setzeBearbeiten(false),
            })
          }
        />
      )}
    </div>
  )
}

/* Eine Funktion und keine Konstante: die Arten kommen aus dem Katalog und
   damit aus einer Abfrage. Auf Modulebene ließe sich die nicht lesen. */
function kundenfelder(arten: { id: string; name: string }[]): Feld[] {
  return [
  { name: 'name', beschriftung: 'Name', pflicht: true, breit: true },
  {
    name: 'art',
    beschriftung: 'Art',
    art: 'auswahl',
    optionen: [
      { wert: '', text: '— keine Angabe —' },
      ...arten.map((a) => ({ wert: a.name, text: a.name })),
    ],
    hinweis: 'Die Auswahl pflegen Sie unter Kataloge.',
  },
  { name: 'kundennummer', beschriftung: 'Kunden-Nr.' },
  { name: 'strasse', beschriftung: 'Straße', breit: true },
  { name: 'plz', beschriftung: 'PLZ' },
  { name: 'ort', beschriftung: 'Ort' },
  { name: 'ansprechpartner', beschriftung: 'Ansprechpartner' },
  { name: 'email', beschriftung: 'E-Mail', art: 'email' },
  { name: 'telefon', beschriftung: 'Telefon', art: 'tel' },
  {
    name: 'eigenkontrolle',
    beschriftung: 'Eigenkontrolle durch den Betreiber',
    art: 'auswahl',
    breit: true,
    optionen: (
      ['keine', 'sicht', 'sicht_und_operativ'] as Eigenkontrolle[]
    ).map((w) => ({ wert: w, text: EIGENKONTROLLETEXT[w] })),
    hinweis:
      'Die regelmäßige Kontrolle ist Pflicht des Betreibers (DIN EN 1176-7). ' +
      'Wer sie im Kundenportal führen darf, entscheiden Sie — Sie kennen die ' +
      'Leute, die dort auf den Platz gehen. Der Abstand wird je Spielplatz ' +
      'festgelegt.',
  },
  {
    name: 'eigenkontrolle_fotos',
    beschriftung: 'Fotos zur Eigenkontrolle',
    art: 'auswahl',
    breit: true,
    optionen: [
      { wert: 'false', text: 'Nein — nur Text' },
      { wert: 'true', text: 'Ja — Bilder erlaubt' },
    ],
    hinweis:
      'Bilder helfen sehr: ohne sie müssen Sie hinfahren, um überhaupt zu ' +
      'sehen, worum es geht. Sie brauchen aber Platz und liegen auf ' +
      'derselben Platte wie die Anwendung — deshalb je Kunde entschieden.',
  },
  {
    name: 'bemerkung',
    beschriftung: 'Bemerkung',
    art: 'mehrzeilig',
    breit: true,
    hinweis: 'Interne Notiz. Erscheint nicht im Bericht.',
  },
  ]
}
